import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import './BatchEditor.css';

/**
 * Batch Editor Component
 * 
 * PROPS:
 * - initialJobName: string (default: 'Job')
 * - initialBatches: array of batch objects (optional)
 * - initialAvailableProcesses: array of process objects (optional)
 * - initialAvailableRecipes: array of recipe objects (optional)
 * - onBatchesChange: function(batches) - called when batch data changes
 * - onJobNameChange: function(jobName) - called when job name changes
 * 
 * Data Structures:
 * 
 * BATCH FORMAT:
 * {
 *   id: 'batch-1',
 *   name: 'Batch 1',
 *   quantity: 40,
 *   partType: 'parts',           // Type of part being processed
 *   partIcon: '📦',             // Icon representing the part
 *   processes: [               // Array of processes in order
 *     {
 *       id: 'process-1-unique-id',
 *       name: 'Process Name',
 *       type: 'prep',
 *       color: '#ff6b6b'
 *     }
 *   ]
 * }
 * 
 * RECIPE FORMAT:
 * {
 *   id: 'recipe-1',
 *   name: 'Recipe Name',
 *   type: 'recipe',
 *   color: '#333',
 *   processes: [               // Processes that will be added when recipe is used
 *     {
 *       id: 'process-1',
 *       name: 'Process Name',
 *       type: 'prep',
 *       color: '#ff6b6b'
 *     }
 *   ]
 * }
 * 
 * PROCESS FORMAT:
 * {
 *   id: 'process-1',
 *   name: 'Process Name',
 *   type: 'prep',              // prep, coating, curing, inspection, finishing
 *   color: '#ff6b6b'           // Color for visual identification
 * }
 */

const BatchEditor = ({ 
  initialJobName = 'Job',
  initialBatches = null,
  initialAvailableProcesses = null,   // Custom processes (optional)
  initialAvailableRecipes = null,     // Custom recipes (optional)
  onBatchesChange = null,             // Callback when batches data changes
  onJobNameChange = null              // Callback when job name changes
}) => {
  const defaultBatches = [
    {
      id: 'batch-1',
      name: 'Batch 1',
      quantity: 40,
      partType: 'parts',
      partIcon: '📦',
      processes: []
    }
  ];

  const defaultAvailableProcesses = [
    { id: 'process-1', name: 'Process A', type: 'prep', color: '#ff6b6b' },
    { id: 'process-2', name: 'Process B', type: 'prep', color: '#4ecdc4' },
    { id: 'process-3', name: 'Process C', type: 'coating', color: '#45b7d1' },
    { id: 'process-4', name: 'Process D', type: 'curing', color: '#96ceb4' },
    { id: 'process-5', name: 'Process E', type: 'inspection', color: '#feca57' },
    { id: 'process-6', name: 'Process F', type: 'coating', color: '#ff9ff3' }
  ];

  const defaultAvailableRecipes = [
    {
      id: 'recipe-1',
      name: 'Standard Recipe',
      type: 'recipe',
      color: '#333',
      processes: [
        { id: 'process-1', name: 'Process A', type: 'prep', color: '#ff6b6b' },
        { id: 'process-2', name: 'Process B', type: 'prep', color: '#4ecdc4' },
        { id: 'process-3', name: 'Process C', type: 'coating', color: '#45b7d1' },
        { id: 'process-4', name: 'Process D', type: 'curing', color: '#96ceb4' },
        { id: 'process-5', name: 'Process E', type: 'inspection', color: '#feca57' }
      ]
    },
    {
      id: 'recipe-2',
      name: 'Extended Recipe',
      type: 'recipe',
      color: '#8e44ad',
      processes: [
        { id: 'process-1', name: 'Process A', type: 'prep', color: '#ff6b6b' },
        { id: 'process-2', name: 'Process B', type: 'prep', color: '#4ecdc4' },
        { id: 'process-3', name: 'Process C', type: 'coating', color: '#45b7d1' },
        { id: 'process-6', name: 'Process F', type: 'coating', color: '#ff9ff3' },
        { id: 'process-4', name: 'Process D', type: 'curing', color: '#96ceb4' },
        { id: 'process-5', name: 'Process E', type: 'inspection', color: '#feca57' }
      ]
    },
    {
      id: 'recipe-3',
      name: 'Basic Recipe',
      type: 'recipe',
      color: '#27ae60',
      processes: [
        { id: 'process-1', name: 'Process A', type: 'prep', color: '#ff6b6b' },
        { id: 'process-2', name: 'Process B', type: 'prep', color: '#4ecdc4' }
      ]
    }
  ];

  const [jobName, setJobName] = useState(initialJobName);
  const [batches, setBatches] = useState(initialBatches || defaultBatches);
  const [availableProcesses, setAvailableProcesses] = useState(initialAvailableProcesses || defaultAvailableProcesses);
  const [availableRecipes, setAvailableRecipes] = useState(initialAvailableRecipes || defaultAvailableRecipes);

  // Update state when props change (for async data loading)
  useEffect(() => {
    if (initialBatches) {
      setBatches(initialBatches);
    }
  }, [initialBatches]);

  useEffect(() => {
    if (initialAvailableProcesses) {
      setAvailableProcesses(initialAvailableProcesses);
    }
  }, [initialAvailableProcesses]);

  useEffect(() => {
    if (initialAvailableRecipes) {
      setAvailableRecipes(initialAvailableRecipes);
    }
  }, [initialAvailableRecipes]);

  useEffect(() => {
    setJobName(initialJobName);
  }, [initialJobName]);

  // Helper function to trigger onChange callbacks
  const updateBatches = (newBatches) => {
    setBatches(newBatches);
    if (onBatchesChange) {
      onBatchesChange(newBatches);
    }
  };

  const updateJobName = (newJobName) => {
    setJobName(newJobName);
    if (onJobNameChange) {
      onJobNameChange(newJobName);
    }
  };

  const createNewBatch = (sourceBatchId, splitQuantity) => {
    const sourceBatch = batches.find(b => b.id === sourceBatchId);
    if (!sourceBatch || splitQuantity <= 0 || splitQuantity >= sourceBatch.quantity) return;

    const newBatchId = `batch-${Date.now()}`;
    const newBatch = {
      id: newBatchId,
      name: `Batch ${batches.length + 1}`,
      quantity: splitQuantity,
      partType: sourceBatch.partType,
      partIcon: sourceBatch.partIcon,
      processes: sourceBatch.processes.map(process => ({
        ...process,
        id: `${process.id}-split-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      })) // Deep copy processes with new unique IDs
    };

    const newBatches = batches.map(batch => 
      batch.id === sourceBatchId 
        ? { ...batch, quantity: batch.quantity - splitQuantity }
        : batch
    ).concat(newBatch);

    updateBatches(newBatches);
  };

  const renameBatch = (batchId, newName) => {
    const newBatches = batches.map(batch =>
      batch.id === batchId ? { ...batch, name: newName } : batch
    );
    updateBatches(newBatches);
  };

  const removeProcessFromBatch = (batchId, processId) => {
    const newBatches = batches.map(batch =>
      batch.id === batchId
        ? { ...batch, processes: batch.processes.filter(p => p.id !== processId) }
        : batch
    );
    updateBatches(newBatches);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const copyProcess = (process, destinationIndex) => {
    // Create a new process with a unique ID for the batch
    return {
      ...process,
      id: `${process.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
  };

  const copyRecipe = (recipe, destinationIndex) => {
    // Create new processes for each process in the recipe
    return recipe.processes.map((process, index) => ({
      ...process,
      id: `${process.id}-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
    }));
  };

  const copy = (source, destination, droppableSource, droppableDestination) => {
    const destClone = Array.from(destination);
    const item = source[droppableSource.index];

    if (item.type === 'recipe') {
      // If it's a recipe, add all its processes
      const newProcesses = copyRecipe(item, droppableDestination.index);
      destClone.splice(droppableDestination.index, 0, ...newProcesses);
    } else {
      // If it's a single process
      const newProcess = copyProcess(item, droppableDestination.index);
      destClone.splice(droppableDestination.index, 0, newProcess);
    }

    return destClone;
  };

  const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    return {
      [droppableSource.droppableId]: sourceClone,
      [droppableDestination.droppableId]: destClone
    };
  };

  const onDragStart = (start) => {
    // This helps prevent layout shifts during drag
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const sourceId = source.droppableId;
    const destinationId = destination.droppableId;

    // Prevent any reordering within template areas (available items)
    if ((sourceId === 'available-processes' || sourceId === 'available-recipes') && 
        (destinationId === 'available-processes' || destinationId === 'available-recipes')) return;

    // Can't move TO available items
    if (destinationId === 'available-processes' || destinationId === 'available-recipes') return;

    // Copying from available processes to a batch
    if (sourceId === 'available-processes') {
      const targetBatchIndex = batches.findIndex(batch => batch.id === destinationId);
      if (targetBatchIndex === -1) return;

      const newProcesses = copy(
        availableProcesses,
        batches[targetBatchIndex].processes,
        source,
        destination
      );

      const newBatches = [...batches];
      newBatches[targetBatchIndex] = {
        ...newBatches[targetBatchIndex],
        processes: newProcesses
      };

      updateBatches(newBatches);
      return;
    }

    // Copying from available recipes to a batch
    if (sourceId === 'available-recipes') {
      const targetBatchIndex = batches.findIndex(batch => batch.id === destinationId);
      if (targetBatchIndex === -1) return;

      const newProcesses = copy(
        availableRecipes,
        batches[targetBatchIndex].processes,
        source,
        destination
      );

      const newBatches = [...batches];
      newBatches[targetBatchIndex] = {
        ...newBatches[targetBatchIndex],
        processes: newProcesses
      };

      updateBatches(newBatches);
      return;
    }

    // Moving within the same batch
    if (sourceId === destinationId) {
      const batchIndex = batches.findIndex(batch => batch.id === sourceId);
      if (batchIndex === -1) return;

      const processesReordered = reorder(
        batches[batchIndex].processes,
        source.index,
        destination.index
      );

      const newBatches = [...batches];
      newBatches[batchIndex] = {
        ...newBatches[batchIndex],
        processes: processesReordered
      };

      updateBatches(newBatches);
      return;
    }

    // Moving between different batches
    const sourceBatchIndex = batches.findIndex(batch => batch.id === sourceId);
    const destBatchIndex = batches.findIndex(batch => batch.id === destinationId);

    if (sourceBatchIndex === -1 || destBatchIndex === -1) return;

    const moveResult = move(
      batches[sourceBatchIndex].processes,
      batches[destBatchIndex].processes,
      source,
      destination
    );

    const newBatches = [...batches];
    newBatches[sourceBatchIndex] = {
      ...newBatches[sourceBatchIndex],
      processes: moveResult[sourceId]
    };
    newBatches[destBatchIndex] = {
      ...newBatches[destBatchIndex],
      processes: moveResult[destinationId]
    };

    updateBatches(newBatches);
  };

  const ProcessComponent = ({ process, index, batchId = null, isTemplate = false }) => (
    <Draggable key={process.id} draggableId={process.id} index={index}>
      {(provided, snapshot) => (
        <React.Fragment>
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`draggable-item process-item ${snapshot.isDragging ? 'dragging' : ''}`}
            style={{
              ...provided.draggableProps.style,
              borderColor: process.color
            }}
          >
            <span {...provided.dragHandleProps} className="drag-handle">⋮⋮</span>
            <span className="process-name">{process.name}</span>
            <span 
              className="process-type"
              style={{ backgroundColor: process.color }}
            >
              {process.type}
            </span>
            {isTemplate && snapshot.isDragging && (
              <span className="copy-indicator">📋</span>
            )}
            {batchId && (
              <button
                onClick={() => removeProcessFromBatch(batchId, process.id)}
                className="remove-item-btn"
                title="Remove process"
              >
                ✕
              </button>
            )}
          </div>
          {isTemplate && snapshot.isDragging && (
            <div className="draggable-item process-item clone-placeholder" style={{ borderColor: process.color }}>
              <span className="drag-handle">⋮⋮</span>
              <span className="process-name">{process.name}</span>
              <span 
                className="process-type"
                style={{ backgroundColor: process.color }}
              >
                {process.type}
              </span>
            </div>
          )}
        </React.Fragment>
      )}
    </Draggable>
  );

  const RecipeComponent = ({ recipe, index, isTemplate = false }) => (
    <Draggable key={recipe.id} draggableId={recipe.id} index={index}>
      {(provided, snapshot) => (
        <React.Fragment>
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`draggable-item recipe-item ${snapshot.isDragging ? 'dragging' : ''}`}
            style={{
              ...provided.draggableProps.style,
              borderColor: recipe.color
            }}
          >
            <span {...provided.dragHandleProps} className="drag-handle">⋮⋮</span>
            <span className="recipe-icon">🧾</span>
            <div className="recipe-info">
              <span className="recipe-name">{recipe.name}</span>
              <span className="recipe-details">{recipe.processes.length} steps</span>
            </div>
            <span 
              className="recipe-type"
              style={{ backgroundColor: recipe.color }}
            >
              RECIPE
            </span>
            {isTemplate && snapshot.isDragging && (
              <span className="copy-indicator">📋</span>
            )}
          </div>
          {isTemplate && snapshot.isDragging && (
            <div className="draggable-item recipe-item clone-placeholder" style={{ borderColor: recipe.color }}>
              <span className="drag-handle">⋮⋮</span>
              <span className="recipe-icon">🧾</span>
              <div className="recipe-info">
                <span className="recipe-name">{recipe.name}</span>
                <span className="recipe-details">{recipe.processes.length} steps</span>
              </div>
              <span 
                className="recipe-type"
                style={{ backgroundColor: recipe.color }}
              >
                RECIPE
              </span>
            </div>
          )}
        </React.Fragment>
      )}
    </Draggable>
  );

  const SplitBatchDialog = ({ batch, onSplit, onCancel }) => {
    const [splitQuantity, setSplitQuantity] = useState(1);
    
    return (
      <div className="split-dialog">
        <h3>Split Batch: {batch.name}</h3>
        <p>Current quantity: {batch.quantity}</p>
        <div className="split-controls">
          <label>
            Split quantity:
            <input 
              type="number" 
              min="1" 
              max={batch.quantity - 1}
              value={splitQuantity}
              onChange={(e) => setSplitQuantity(parseInt(e.target.value) || 1)}
            />
          </label>
          <div className="split-preview">
            Will create: {batch.quantity - splitQuantity} + {splitQuantity}
          </div>
          <div className="split-buttons">
            <button onClick={() => onSplit(splitQuantity)} className="split-btn">Split</button>
            <button onClick={onCancel} className="cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const [splitDialog, setSplitDialog] = useState(null);

  const totalQuantity = batches.reduce((sum, batch) => sum + batch.quantity, 0);

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="batch-editor">
        <div className="batch-editor-content">
          <div className="available-items-section">
            <div className="available-recipes-section">
              <h3>Recipes</h3>
              <Droppable droppableId="available-recipes" isDropDisabled={true} type="ITEM">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="available-recipes"
                    style={{ minHeight: '120px' }}
                  >
                    {availableRecipes.map((recipe, index) => (
                      <RecipeComponent 
                        key={recipe.id} 
                        recipe={recipe} 
                        index={index}
                        isTemplate={true}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            <div className="available-processes-section">
              <h3>Processes</h3>
              <Droppable droppableId="available-processes" isDropDisabled={true} type="ITEM">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="available-processes"
                    style={{ minHeight: '100px' }}
                  >
                    {availableProcesses.map((process, index) => (
                      <ProcessComponent 
                        key={process.id} 
                        process={process} 
                        index={index}
                        isTemplate={true}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          <div className="batches-section">
            <h3>Batches ({batches.length})</h3>
            <div className="batches-container">
              {batches.map(batch => (
                <div key={batch.id} className="batch-container">
                  <div className="batch-header">
                    <div className="batch-title">
                      <span className="batch-part-icon">{batch.partIcon}</span>
                      <input
                        type="text"
                        value={batch.name}
                        onChange={(e) => renameBatch(batch.id, e.target.value)}
                        className="batch-name-input"
                      />
                    </div>
                    <div className="batch-controls">
                      <span className="batch-quantity">Qty: {batch.quantity}</span>
                      <button
                        onClick={() => setSplitDialog(batch)}
                        className="split-batch-btn"
                        title="Split batch"
                      >
                        ✂️
                      </button>
                    </div>
                  </div>

                  <Droppable droppableId={batch.id} type="ITEM">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`batch-items ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                        style={{ minHeight: '120px' }}
                      >
                        {batch.processes.length === 0 ? (
                          <div className="empty-batch">
                            <p>Drop recipes or processes here</p>
                            <div className="drop-zone-indicator">⚙️ 🧾</div>
                          </div>
                        ) : (
                          batch.processes.map((process, index) => (
                            <ProcessComponent 
                              key={process.id} 
                              process={process} 
                              index={index}
                              batchId={batch.id}
                            />
                          ))
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </div>
        </div>

        {splitDialog && (
          <div className="modal-overlay">
            <SplitBatchDialog 
              batch={splitDialog}
              onSplit={(quantity) => {
                createNewBatch(splitDialog.id, quantity);
                setSplitDialog(null);
              }}
              onCancel={() => setSplitDialog(null)}
            />
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default BatchEditor; 